// 品質控制系統資料模型
// Quality Control System Data Model

import { products } from './inventoryData';
import { purchaseOrders, purchaseReceipts } from './purchaseOrderData';
import { productionOrders } from './productionData';

// 品質檢驗類型
export const inspectionTypes = [
  { id: 'incoming', name: '進料檢驗', name_en: 'Incoming Inspection', icon: 'PackageCheck', color: '#3B82F6' },
  { id: 'in-process', name: '製程檢驗', name_en: 'In-Process Inspection', icon: 'Microscope', color: '#8B5CF6' },
  { id: 'final', name: '成品檢驗', name_en: 'Final Inspection', icon: 'ClipboardCheck', color: '#10B981' },
  { id: 'outgoing', name: '出貨檢驗', name_en: 'Outgoing Inspection', icon: 'Truck', color: '#F59E0B' },
  { id: 'periodic', name: '定期檢驗', name_en: 'Periodic Inspection', icon: 'CalendarCheck', color: '#EC4899' },
];

// 品質檢驗結果
export const inspectionResults = [
  { id: 'passed', name: '合格', name_en: 'Passed', color: '#10B981', icon: 'CheckCircle' },
  { id: 'failed', name: '不合格', name_en: 'Failed', color: '#EF4444', icon: 'XCircle' },
  { id: 'conditional', name: '條件合格', name_en: 'Conditionally Passed', color: '#F59E0B', icon: 'AlertCircle' },
  { id: 'pending', name: '待定', name_en: 'Pending', color: '#6B7280', icon: 'Clock' },
];

// 檢驗方法
export const inspectionMethods = [
  { id: 'visual', name: '目視檢查', name_en: 'Visual Inspection' },
  { id: 'measurement', name: '尺寸測量', name_en: 'Dimensional Measurement' },
  { id: 'functional', name: '功能測試', name_en: 'Functional Testing' },
  { id: 'chemical', name: '化學分析', name_en: 'Chemical Analysis' },
  { id: 'mechanical', name: '機械測試', name_en: 'Mechanical Testing' },
  { id: 'electrical', name: '電氣測試', name_en: 'Electrical Testing' },
  { id: 'weight', name: '重量檢查', name_en: 'Weight Check' },
  { id: 'durability', name: '耐久性測試', name_en: 'Durability Testing' },
];

// 品質標準
export const qualityStandards = [
  { id: 'iso-9001', name: 'ISO 9001', description: '品質管理系統標準' },
  { id: 'iso-14001', name: 'ISO 14001', description: '環境管理系統標準' },
  { id: 'iso-13485', name: 'ISO 13485', description: '醫療器材品質管理系統標準' },
  { id: 'iatf-16949', name: 'IATF 16949', description: '汽車產業品質管理系統標準' },
  { id: 'gmp', name: 'GMP', description: '優良製造規範' },
  { id: 'haccp', name: 'HACCP', description: '危害分析重要管制點' },
  { id: 'internal', name: '內部標準', description: '公司內部品質標準' },
];

// 檢驗計劃
export const inspectionPlans = [
  {
    id: 'qcp-001',
    name: '原材料檢驗計劃',
    name_en: 'Raw Materials Inspection Plan',
    description: '針對所有進料原材料的標準檢驗程序',
    product_categories: ['raw-material'],
    inspection_type: 'incoming',
    sampling_method: 'AQL 2.5',
    required_standards: ['iso-9001', 'internal'],
    created_at: '2024-12-01',
    updated_at: '2025-01-15',
    active: true,
    checkpoints: [
      { id: 'cp-001-01', name: '外觀檢查', method: 'visual', criteria: '無明顯瑕疵、變形或異物' },
      { id: 'cp-001-02', name: '尺寸檢查', method: 'measurement', criteria: '符合規格±0.5mm' },
      { id: 'cp-001-03', name: '重量檢查', method: 'weight', criteria: '標稱重量±2%' },
      { id: 'cp-001-04', name: '材質檢查', method: 'chemical', criteria: '符合材質證明書' },
    ]
  },
  {
    id: 'qcp-002',
    name: '半成品檢驗計劃',
    name_en: 'Semi-finished Products Inspection Plan',
    description: '生產過程中半成品的檢驗程序',
    product_categories: ['semi-finished'],
    inspection_type: 'in-process',
    sampling_method: '每批抽樣10%',
    required_standards: ['iso-9001', 'internal'],
    created_at: '2024-12-05',
    updated_at: '2025-01-20',
    active: true,
    checkpoints: [
      { id: 'cp-002-01', name: '加工精度檢查', method: 'measurement', criteria: '符合工程圖±0.2mm' },
      { id: 'cp-002-02', name: '表面處理檢查', method: 'visual', criteria: '無刮傷、凹痕或其他表面缺陷' },
      { id: 'cp-002-03', name: '組裝檢查', method: 'functional', criteria: '組件可正確安裝且無鬆動' },
    ]
  },
  {
    id: 'qcp-003',
    name: '成品檢驗計劃',
    name_en: 'Finished Products Inspection Plan',
    description: '最終產品出貨前的全面檢驗程序',
    product_categories: ['finished'],
    inspection_type: 'final',
    sampling_method: 'AQL 1.0',
    required_standards: ['iso-9001', 'iatf-16949', 'internal'],
    created_at: '2024-12-10',
    updated_at: '2025-02-01',
    active: true,
    checkpoints: [
      { id: 'cp-003-01', name: '外觀檢查', method: 'visual', criteria: '符合產品規格書要求，無外觀缺陷' },
      { id: 'cp-003-02', name: '功能測試', method: 'functional', criteria: '所有功能正常運作' },
      { id: 'cp-003-03', name: '包裝檢查', method: 'visual', criteria: '包裝完整，標籤正確' },
      { id: 'cp-003-04', name: '耐久性測試', method: 'durability', criteria: '符合產品壽命要求' },
      { id: 'cp-003-05', name: '安全測試', method: 'electrical', criteria: '符合安全標準要求' },
    ]
  },
];

// 檢驗記錄
export const inspectionRecords = [
  {
    id: 'insp-001',
    inspection_type: 'incoming',
    reference_id: 'rcpt-001', // 對應採購收貨單
    reference_type: 'purchase_receipt',
    product_id: 'prod-001',
    quantity_inspected: 500,
    quantity_passed: 480,
    quantity_failed: 20,
    inspector: '王品管',
    inspection_date: '2025-06-15',
    plan_id: 'qcp-001',
    result: 'conditional',
    notes: '部分材料有輕微表面瑕疵，但不影響使用功能，已與供應商溝通改善',
    checkpoint_results: [
      { checkpoint_id: 'cp-001-01', result: 'failed', value: '發現表面劃痕', notes: '約5%的材料有明顯劃痕' },
      { checkpoint_id: 'cp-001-02', result: 'passed', value: '符合規格', notes: '' },
      { checkpoint_id: 'cp-001-03', result: 'passed', value: '標準重量+1.2%', notes: '在允許範圍內' },
      { checkpoint_id: 'cp-001-04', result: 'passed', value: '符合要求', notes: '已核對材質證明書' },
    ],
    images: [
      { id: 'img-001-1', url: 'https://images.unsplash.com/photo-1581092921461-eab10380ed66?w=800&h=600&fit=crop', description: '材料表面瑕疵照片' },
      { id: 'img-001-2', url: 'https://images.unsplash.com/photo-1581093196277-9f608bb3b4b9?w=800&h=600&fit=crop', description: '測量過程照片' },
    ],
    corrective_actions: [
      { id: 'ca-001-1', action: '要求供應商改善生產過程，避免表面劃痕', responsible: '採購部', deadline: '2025-06-30', status: 'in-progress' },
      { id: 'ca-001-2', action: '增加進料檢驗頻率', responsible: '品管部', deadline: '2025-06-20', status: 'completed' },
    ]
  },
  {
    id: 'insp-002',
    inspection_type: 'in-process',
    reference_id: 'prod-order-002', // 對應生產訂單
    reference_type: 'production_order',
    product_id: 'prod-005',
    quantity_inspected: 200,
    quantity_passed: 195,
    quantity_failed: 5,
    inspector: '李檢查',
    inspection_date: '2025-06-18',
    plan_id: 'qcp-002',
    result: 'passed',
    notes: '生產過程穩定，少量產品需要返工',
    checkpoint_results: [
      { checkpoint_id: 'cp-002-01', result: 'passed', value: '符合公差要求', notes: '' },
      { checkpoint_id: 'cp-002-02', result: 'passed', value: '表面處理良好', notes: '少量產品有輕微瑕疵，已返工處理' },
      { checkpoint_id: 'cp-002-03', result: 'passed', value: '組裝正確', notes: '' },
    ],
    images: [
      { id: 'img-002-1', url: 'https://images.unsplash.com/photo-1581093458791-9d1ec2afc9f7?w=800&h=600&fit=crop', description: '製程檢驗照片' },
      { id: 'img-002-2', url: 'https://images.unsplash.com/photo-1581093196277-9f608bb3b4b9?w=800&h=600&fit=crop', description: '測量結果照片' },
    ],
    corrective_actions: []
  },
  {
    id: 'insp-003',
    inspection_type: 'final',
    reference_id: 'prod-order-005', // 對應生產訂單
    reference_type: 'production_order',
    product_id: 'prod-010',
    quantity_inspected: 100,
    quantity_passed: 98,
    quantity_failed: 2,
    inspector: '張品質',
    inspection_date: '2025-06-20',
    plan_id: 'qcp-003',
    result: 'passed',
    notes: '產品品質良好，符合出貨標準',
    checkpoint_results: [
      { checkpoint_id: 'cp-003-01', result: 'passed', value: '外觀良好', notes: '' },
      { checkpoint_id: 'cp-003-02', result: 'passed', value: '功能正常', notes: '' },
      { checkpoint_id: 'cp-003-03', result: 'passed', value: '包裝完整', notes: '' },
      { checkpoint_id: 'cp-003-04', result: 'passed', value: '耐久性測試通過', notes: '' },
      { checkpoint_id: 'cp-003-05', result: 'passed', value: '安全測試通過', notes: '' },
    ],
    images: [
      { id: 'img-003-1', url: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&h=600&fit=crop', description: '成品檢驗照片' },
      { id: 'img-003-2', url: 'https://images.unsplash.com/photo-1581093458791-9d1ec2afc9f7?w=800&h=600&fit=crop', description: '功能測試照片' },
    ],
    corrective_actions: []
  },
];

// 不合格品處理記錄
export const nonconformanceRecords = [
  {
    id: 'nc-001',
    inspection_id: 'insp-001',
    product_id: 'prod-001',
    quantity: 20,
    detected_date: '2025-06-15',
    detected_by: '王品管',
    description: '原材料表面有明顯劃痕，不符合外觀要求',
    severity: 'minor', // minor, major, critical
    disposition: 'return-to-supplier', // return-to-supplier, rework, scrap, use-as-is
    disposition_date: '2025-06-16',
    approved_by: '陳經理',
    notes: '已與供應商協調退貨事宜',
    images: [
      { id: 'nc-img-001-1', url: 'https://images.unsplash.com/photo-1581092921461-eab10380ed66?w=800&h=600&fit=crop', description: '不合格品照片' },
    ],
    corrective_actions: [
      { id: 'nc-ca-001-1', action: '要求供應商提供改善計劃', responsible: '採購部', deadline: '2025-06-30', status: 'in-progress' },
    ]
  },
  {
    id: 'nc-002',
    inspection_id: 'insp-002',
    product_id: 'prod-005',
    quantity: 5,
    detected_date: '2025-06-18',
    detected_by: '李檢查',
    description: '半成品表面處理不均勻',
    severity: 'minor',
    disposition: 'rework',
    disposition_date: '2025-06-19',
    approved_by: '陳經理',
    notes: '已安排返工處理',
    images: [
      { id: 'nc-img-002-1', url: 'https://images.unsplash.com/photo-1581093458791-9d1ec2afc9f7?w=800&h=600&fit=crop', description: '不合格品照片' },
    ],
    corrective_actions: [
      { id: 'nc-ca-002-1', action: '調整表面處理工藝參數', responsible: '生產部', deadline: '2025-06-25', status: 'completed' },
    ]
  },
];

// 品質控制儀表板數據
export const qualityDashboardData = {
  kpis: [
    { id: 'kpi-001', name: '一次通過率', value: 96.5, unit: '%', trend: 'up', target: 98 },
    { id: 'kpi-002', name: '不合格品率', value: 1.8, unit: '%', trend: 'down', target: 1.5 },
    { id: 'kpi-003', name: '客戶投訴率', value: 0.5, unit: '%', trend: 'stable', target: 0.5 },
    { id: 'kpi-004', name: '平均檢驗時間', value: 45, unit: '分鐘', trend: 'down', target: 40 },
  ],
  inspectionSummary: {
    total: 125,
    passed: 110,
    failed: 8,
    conditional: 7,
    byType: [
      { type: 'incoming', total: 45, passed: 38, failed: 4, conditional: 3 },
      { type: 'in-process', total: 50, passed: 47, failed: 2, conditional: 1 },
      { type: 'final', total: 30, passed: 25, failed: 2, conditional: 3 },
    ]
  },
  nonconformanceSummary: {
    total: 15,
    bySeverity: [
      { severity: 'minor', count: 10 },
      { severity: 'major', count: 4 },
      { severity: 'critical', count: 1 },
    ],
    byDisposition: [
      { disposition: 'return-to-supplier', count: 5 },
      { disposition: 'rework', count: 7 },
      { disposition: 'scrap', count: 2 },
      { disposition: 'use-as-is', count: 1 },
    ]
  },
  trendData: {
    labels: ['1月', '2月', '3月', '4月', '5月', '6月'],
    datasets: [
      {
        name: '一次通過率',
        data: [94.2, 94.8, 95.3, 95.9, 96.2, 96.5],
        color: '#10B981'
      },
      {
        name: '不合格品率',
        data: [3.1, 2.8, 2.5, 2.2, 2.0, 1.8],
        color: '#EF4444'
      },
    ]
  },
  recentIssues: [
    { id: 'issue-001', date: '2025-06-15', product: '高碳鋼板', issue: '表面劃痕', status: 'in-progress' },
    { id: 'issue-002', date: '2025-06-12', product: '電路板組件', issue: '焊接不良', status: 'resolved' },
    { id: 'issue-003', date: '2025-06-10', product: '塑膠外殼', issue: '顏色不均', status: 'resolved' },
    { id: 'issue-004', date: '2025-06-08', product: '軸承', issue: '尺寸超差', status: 'resolved' },
  ]
};

